import { SelectionModel } from '@angular/cdk/collections';
import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';

import {
    CompanyParameters,
    CompanyParametersQuery,
    PrintingAvailableColumn, PrintingPatternBO, PrintingPatternConfigBase,
    withModule
} from '@skylab-shared/business';
import { ValueConverter } from '@skylab-shared/core';
import { DocumentPrintingPatternModel, EnumTranslatorBase, EnumValue, InventoryDocumentKind, LabeledEnumValue, LabeledEnumValueConverter, PrintingPatternGridColumnDefinition, PrintingPatternGridDefinition, PrintingPatternLanguage, PrintingPatternModel, SaleDocumentKind } from '@skylab-shared/dataModel';

import { PrintingPatternConfig, PrintingPatternRepository, PrintingPatternRepositoryBase } from '@skylab-angular-shared/business';
import {
    CommandBus, EnumTranslator, EventBus,
    EventBusBase, L10n,
    L10nBase, RootContext,
    RootContextBase, Weaver,
    WeaverBase
} from '@skylab-angular-shared/core';
import {
    BooleanValueAccessorDirective,
    ButtonComponent,
    DialogContentComponentIns,
    DialogHeaderComponentIns,
    DialogInsService, FocusByDirective, FormContainerComponent, GridInsComponent, GridTemplateDirective, InsertionDirective, SelectValueAccessorDirective, ServiceGridColumn, ServiceGridOptions, TextareaValueAccessorDirective, TextValueAccessorDirective, ToastService,
    ValidateDirective,
    ValidationSourceDirective
} from '@skylab-angular-shared/ui';

import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { Dictionary } from '@skylab-shared/types';
import { BehaviorSubject } from 'rxjs';
import { GridMoveDirection, GridMoveRowCellComponent } from '../../grid/grid-move-cell/grid-move-row-cell.component';
import { DictionaryDialogComponentBase, DictionaryDialogViewModelBase } from '../dictionary-dialog-base';
import { PrintingPatternDialogGridWidth } from './grid/printing-pattern-dialog-grid-width-cell.component';
import { PrintingPatternDialogGridColumnIdentitiy, PrintingPatternDialogGridCustomTemplates, PrintingPatternDialogGridService } from './grid/printing-pattern-dialog-grid.service';


export interface ChosenColumn {
    order: number;
    id: string;
    name: string;
    width: number;
}

interface PrintingPatternViewModel extends DictionaryDialogViewModelBase<PrintingPatternBO> {
    companyParameters: CompanyParameters | null;

    allPrintingPatternColumnsCollection: PrintingAvailableColumn[];
    availablePrintingPatternColumnsCollection: PrintingAvailableColumn[];

    printingPatternTypes: { label: string; value: string }[];
    printingPatternTypeValueConverter: ValueConverter<string, { label: string; value: string }>;
    secondaryLanguages: LabeledEnumValue[];
    secondaryLanguageValueConverter: LabeledEnumValueConverter<EnumValue>;

    isAdvancePattern(): boolean;
    showParameter(parameter: string): boolean;
    onAdd(event: CustomEvent<{ value: ChosenColumn }>): void;
    onPatternTypeChange(newValue: string): void;

    gridOptions: ServiceGridOptions<ChosenColumn, PrintingPatternDialogGridColumnIdentitiy, unknown>;
    gridColumns$: BehaviorSubject<ServiceGridColumn<PrintingPatternDialogGridColumnIdentitiy, unknown, ChosenColumn, unknown, PrintingPatternDialogGridCustomTemplates>[]>;
    gridDataSource$: BehaviorSubject<ChosenColumn[]>;
}

type PrintingPatterIncludes = keyof PrintingPatternModel['includes'];

@Component({
    selector: 'skl-printing-pattern-dialog',
    templateUrl: './printing-pattern-dialog.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [DialogContentComponentIns, DialogHeaderComponentIns, FormContainerComponent, ValidationSourceDirective, SelectValueAccessorDirective, FormsModule, ValidateDirective, TextValueAccessorDirective, FocusByDirective, BooleanValueAccessorDirective, NgClass, GridInsComponent, GridTemplateDirective, PrintingPatternDialogGridWidth, GridMoveRowCellComponent, InsertionDirective, TextareaValueAccessorDirective, ButtonComponent, TranslatePipe]
})
export class PrintingPatternDialogComponent extends DictionaryDialogComponentBase<
PrintingPatternModel,
PrintingPatternBO,
PrintingPatternRepositoryBase,
PrintingPatternViewModel
> {
    // dependencies
    public constructor(
        @Inject(PrintingPatternRepository) printingPatternRepositoryBase: PrintingPatternRepositoryBase,
        @Inject(L10n) l10n: L10nBase,
        @Inject(Weaver) weaver: WeaverBase,
        @Inject(RootContext) rootContext: RootContextBase,
        @Inject(EventBus) eventBus: EventBusBase,
        @Inject(EnumTranslator) private readonly enumTranslator: EnumTranslatorBase,
        @Inject(PrintingPatternConfig) private readonly printingPatternConfig: PrintingPatternConfigBase,
        private readonly printingPatternDialogGridService: PrintingPatternDialogGridService,
        toastService: ToastService,
        commandBus: CommandBus,
        dialogService: DialogInsService,
    ) {
        super(
            printingPatternRepositoryBase,
            toastService,
            l10n,
            weaver,
            rootContext,
            commandBus,
            eventBus,
            dialogService
        );
    }

    // #region init

    protected override getInitialState(): PrintingPatternViewModel {
        const viewModel = {
            ...super.getInitialState(),
            companyParameters: null,

            allPrintingPatternColumnsCollection: [] as PrintingAvailableColumn[],
            availablePrintingPatternColumnsCollection: [] as PrintingAvailableColumn[],

            printingPatternTypeValueConverter: {
                convert: (item: string) => this.viewModel.printingPatternTypes.find(x => x.value === item),
                convertBack: (item: { label: string; value: string } | undefined) => item ? item.value : '',
            },
            printingPatternTypes: [] as { label: string; value: string }[],

            secondaryLanguageValueConverter: new LabeledEnumValueConverter(this.enumTranslator, PrintingPatternLanguage),
            secondaryLanguages: this.enumTranslator.getLabeledEnumValueArray(PrintingPatternLanguage),
            isAdvancePattern: this.isAdvancePattern.bind(this),
            showParameter: this.showParameter.bind(this),
            onPatternTypeChange: this.onPatternTypeChangedAsync.bind(this),
            onAdd: (event: CustomEvent<{ value: ChosenColumn }>) => {
                const availbableColumn = event.detail.value;
                if (!availbableColumn) return;
                this.onAddPrintingPatternColumn(availbableColumn.id, availbableColumn.name, 2);
                this.viewModel.gridOptions.api?.gridControl?.scrollToBottom?.();
            },
        } as any as PrintingPatternViewModel;

        viewModel.gridOptions = {
            selection: new SelectionModel<ChosenColumn>(true, []),
            viewType: 'table-virtual',
            virtualizationConfig: {
                maxRows: 8,
                rowHeight: 50,
            }
        };
        viewModel.gridDataSource$ = new BehaviorSubject<ChosenColumn[]>([]);

        viewModel.gridColumns$ = new BehaviorSubject(this.printingPatternDialogGridService.getGridColumns({
            remove: (entity: ChosenColumn) => {
                this.onRemovePrintingPatternColumn(entity);
            }}
        ));

        return viewModel;
    }

    protected override async preloadDataAsync(): Promise<void> {
        await this.localContext.preload(CompanyParametersQuery);
        this.viewModel.companyParameters = this.localContext.getPreloaded(CompanyParametersQuery) || null;
    }

    protected override async prepareViewModelAsync(): Promise<void> {
        await super.prepareViewModelAsync();

        this.setPrintingPatternsTypes();

        this.refreshAllPrintingPatternColumnsCollection();
        this.assignColumnsCollectionFromModel();
    }

    protected override async provideBusinessObjectAsync(): Promise<PrintingPatternBO> {
        try {
            if (this.data && this.data.id != null) {
                return await this.repo.loadAsync(this.data.id, this.localContext);
            } else {
                const bo = await this.repo.createNewAsync(this.localContext);

                const defaults = this.printingPatternConfig.getPrintingPatternConfig(SaleDocumentKind.Invoice, !!this.viewModel.companyParameters?.vatPayer).getDefaults();

                bo.model.default = defaults.default;
                bo.model.disableShadows = defaults.disableShadows;
                bo.model.note = defaults.note;
                bo.model.secondaryLanguage = defaults.secondaryLanguage;
                bo.model.name = defaults.name;
                bo.model.kind = defaults.kind;
                bo.model.documentTitle = defaults.documentTitle;
                bo.model.itemsList = new PrintingPatternGridDefinition(defaults.itemsList);
                bo.model.includes = new DocumentPrintingPatternModel(defaults.includes);

                return bo;
            }
        } catch (err) {
            this.logger.error(err);
            throw err;
        }
    }

    private setPrintingPatternsTypes(): void {
        let types = this.printingPatternConfig.printingPatternTypes;

        if (!(this.viewModel.companyParameters?.vatPayer || (this.data && this.data.id != null))) {
            types = types.filter(t => ![SaleDocumentKind.VatMarginInvoice, SaleDocumentKind.VatMarginInvoiceAdjustment].includes(<SaleDocumentKind>t.value));
        }

        if (!withModule('Inventory')) {
            types = types.filter(elem => !(elem.value in InventoryDocumentKind));
        }

        this.viewModel.printingPatternTypes = types;
    }

    private isAdvancePattern(): boolean {
        return this.viewModel.bo.model.kind === SaleDocumentKind.AdvanceInvoice;
    }

    private async onPatternTypeChangedAsync(): Promise<void> {
        try {
            const defaults = this.printingPatternConfig.getPrintingPatternConfig(<SaleDocumentKind>this.viewModel.bo.model.kind, !!this.viewModel.companyParameters?.vatPayer).getDefaults();

            this.viewModel.bo.model.secondaryLanguage = defaults.secondaryLanguage;
            this.viewModel.bo.model.name = defaults.name;
            this.viewModel.bo.model.kind = defaults.kind;
            this.viewModel.bo.model.documentTitle = defaults.documentTitle;
            this.viewModel.bo.model.itemsList = new PrintingPatternGridDefinition(defaults.itemsList);
            this.viewModel.bo.model.includes = new DocumentPrintingPatternModel(defaults.includes);

            this.refreshAllPrintingPatternColumnsCollection();
            this.assignColumnsCollectionFromModel();
        } catch (err) {
            this.logger.error(err);
        }
    }
    // #endregion

    // #region collection

    private refreshAllPrintingPatternColumnsCollection(): void {
        this.viewModel.allPrintingPatternColumnsCollection = this.printingPatternConfig.getPrintingPatternConfig(<SaleDocumentKind>this.viewModel.bo.model.kind, !!this.viewModel.companyParameters?.vatPayer).columns;
        this.refreshAvailablePrintingPatternColumns();
    }

    private assignColumnsCollectionFromModel(): void {
        this.viewModel.gridDataSource$.next([]);
        this.viewModel.bo.model.itemsList.columns.forEach(column => {
            const availableColumn = this.viewModel.allPrintingPatternColumnsCollection.find(ac => ac.id === column.id);
            if (availableColumn) {
                this.onAddPrintingPatternColumn(availableColumn.id, availableColumn.name, column.width);
            }
        });
    }

    private onAddPrintingPatternColumn(id: string, name: string, width: number): void {
        const newColumn: ChosenColumn = {
            order: this.viewModel.gridDataSource$.value.length + 1,
            id: id,
            name: name,
            width: width,
        };
        this.viewModel.gridDataSource$.next([...this.viewModel.gridDataSource$.value, newColumn]);
        this.refreshAvailablePrintingPatternColumns();
    }

    private onRemovePrintingPatternColumn(entity: ChosenColumn): void {
        const index = this.viewModel.gridDataSource$.value.indexOf(entity);
        if (index != -1) {
            this.viewModel.gridDataSource$.value.splice(index, 1);
            this.viewModel.gridDataSource$.next([...this.viewModel.gridDataSource$.value]);
            this.refreshAvailablePrintingPatternColumns();
        }
    }

    private refreshAvailablePrintingPatternColumns(): void {
        const availableColumns = this.viewModel.allPrintingPatternColumnsCollection.filter(
            ac => !this.viewModel.gridDataSource$.value.some(c => c.id === ac.id),
        );
        this.viewModel.availablePrintingPatternColumnsCollection = [...availableColumns];
        this.recalculateLp();
    }

    public swapPrintingPatternColumn(direction: GridMoveDirection, entity: ChosenColumn): void {
        const selectedItemIndex = this.viewModel.gridDataSource$.value.findIndex(elem => elem === entity);
        const to = selectedItemIndex + (direction === 'down' ? 1 : -1);

        this.viewModel.gridDataSource$.value.splice(to, 0, this.viewModel.gridDataSource$.value.splice(selectedItemIndex, 1)[0]);
        this.viewModel.gridDataSource$.next([...this.viewModel.gridDataSource$.value]);

        this.recalculateLp();
    }

    private recalculateLp(): void {
        for (let i = 0; i < this.viewModel.gridDataSource$.value.length; i++) {
            this.viewModel.gridDataSource$.value[i].order = i + 1;
        }
    }

    // #endregion
    // #region parameter

    private showParameter(parameter: PrintingPatterIncludes): boolean {
        if (!this.viewModel.isFormReady) return false;

        const patternType = this.viewModel.bo.model.kind;
        const isInventoryDocument = patternType in InventoryDocumentKind;

        return (<Dictionary<boolean>>{
            explainSaleDate: !isInventoryDocument && patternType !== SaleDocumentKind.ProForma && patternType !== SaleDocumentKind.AdvanceInvoice,
            vatTable: !!this.viewModel.companyParameters?.vatPayer && ![SaleDocumentKind.VatMarginInvoice, SaleDocumentKind.VatMarginInvoiceAdjustment].includes(patternType as SaleDocumentKind),
            baseCurrencyVatTable: !!this.viewModel.companyParameters?.vatPayer && (patternType === SaleDocumentKind.ProForma || isInventoryDocument),
            vatTableWhenReverseCharge: !isInventoryDocument && !!this.viewModel.companyParameters?.vatPayer && [SaleDocumentKind.Invoice, SaleDocumentKind.InvoiceAdjustment, SaleDocumentKind.AdvanceInvoice, SaleDocumentKind.AdvanceInvoiceAdjustment].includes(patternType as SaleDocumentKind),
            sourceDocumentInfo: !isInventoryDocument && patternType === SaleDocumentKind.Invoice || patternType === SaleDocumentKind.Receipt,
            jpkCodes: !!this.viewModel.companyParameters?.vatPayer,
            previousAdvancesList: !isInventoryDocument && patternType === SaleDocumentKind.AdvanceInvoice,
            ksefNumber: !isInventoryDocument && ![SaleDocumentKind.ProForma, SaleDocumentKind.Receipt, SaleDocumentKind.ReceiptAdjustment].includes(patternType as SaleDocumentKind),
            leftToPay: !isInventoryDocument,
            payments: !isInventoryDocument,
            unsettledDocuments: !isInventoryDocument,
            deliveryAddress: ![InventoryDocumentKind.GoodsReceipt, InventoryDocumentKind.GoodsReceiptAdjustment, InventoryDocumentKind.InternalGoodsIssue, InventoryDocumentKind.InternalGoodsReceipt].includes(patternType as InventoryDocumentKind),
            summary: isInventoryDocument
        })[parameter] ?? true;
    }

    // #endregion

    public override async onConfirmAsync(): Promise<void> {
        this.viewModel.bo.model.itemsList.columns.clear();
        this.viewModel.gridDataSource$.value.forEach(c => {
            const columnConfig = new PrintingPatternGridColumnDefinition();
            columnConfig.width = c.width;
            columnConfig.id = c.id;
            this.viewModel.bo.model.itemsList.columns.push(columnConfig);
        });
        super.onConfirmAsync();
    }
}
